<template>
  <div class="container py-3">
    <!-- Page Header -->
    <div class="page-header d-flex justify-content-between align-items-center mb-4">
      <h2>{{ $t('user.title') }}</h2>
      <button class="btn btn-primary" @click="openAddModal">
        <i class="bi bi-person-plus-fill me-1"></i> {{ $t('user.add') }}
      </button>
    </div>

    <!-- Search & Filters -->
    <div class="search-wrapper mb-3">
      <div class="input-group">
        <span class="input-group-text bg-white border-end-0">🔍</span>
        <input
          v-model="search"
          type="text"
          class="form-control border-start-0"
          :placeholder="$t('user.searchPlaceholder')"
        />
      </div>
    </div>

    <!-- Users Table -->
    <div class="table-card card border-0 shadow-sm">
      <div class="table-responsive">
        <table class="premium-table table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>{{ $t('user.username') }}</th>
              <th>{{ $t('user.email') }}</th>
              <th>{{ $t('user.role') }}</th>
              <th>{{ $t('user.status') }}</th>
              <th class="text-end">{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="users.length === 0">
              <td colspan="5">
                <div class="empty-state text-center py-5">
                  <div class="empty-icon fs-1 mb-2">👥</div>
                  <p class="text-muted">{{ $t('user.noUsers') }}</p>
                </div>
              </td>
            </tr>
            <tr v-for="user in users" :key="user.id">
              <td>
                <div class="d-flex align-items-center">
                  <div class="avatar-circle me-2 bg-light-primary text-primary font-weight-bold">
                    {{ user.username.charAt(0).toUpperCase() }}
                  </div>
                  <strong>{{ user.username }}</strong>
                </div>
              </td>
              <td>{{ user.email }}</td>
              <td>
                <span
                  class="badge rounded-pill"
                  :class="user.role_name === 'Admin' ? 'bg-primary' : 'bg-secondary'"
                >
                  {{ user.role_name || $t('user.noRole') }}
                </span>
              </td>
              <td>
                <span
                  class="badge"
                  :class="user.is_active ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'"
                >
                  {{ user.is_active ? $t('user.active') : $t('user.inactive') }}
                </span>
              </td>
              <td class="text-end">
                <button
                  class="btn btn-sm btn-outline-warning me-2"
                  @click="openEditModal(user)"
                >
                  <i class="bi bi-pencil-fill me-1"></i> {{ $t('common.edit') }}
                </button>
                <button
                  class="btn btn-sm"
                  :class="user.is_active ? 'btn-outline-danger' : 'btn-outline-success'"
                  @click="toggleUserStatus(user)"
                >
                  <i
                    class="bi"
                    :class="user.is_active ? 'bi-person-x-fill' : 'bi-person-check-fill'"
                  ></i>
                  {{ user.is_active ? $t('user.deactivate') : $t('user.activate') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <MainPagination
      v-model:currentPage="currentPage"
      v-model:pageSize="pageSize"
      :total="totalRecords"
      class="mt-3"
    />

    <!-- Add/Edit User Modal -->
    <div class="modal fade" ref="userModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg">
          <form @submit.prevent="saveUser">
            <div class="modal-header bg-light border-0">
              <h5 class="modal-title font-weight-bold">
                {{ isEditing ? $t('user.edit') : $t('user.add') }}
              </h5>
              <button type="button" class="btn-close" @click="hideUserModal"></button>
            </div>
            <div class="modal-body py-4">
              <div v-if="errorMessage" class="alert alert-danger py-2 mb-3">
                {{ errorMessage }}
              </div>

              <!-- Username -->
              <div class="mb-3">
                <label class="form-label font-weight-bold">{{ $t('user.username') }}</label>
                <input
                  v-model="form.username"
                  type="text"
                  class="form-control"
                  required
                  :disabled="isEditing"
                  :placeholder="$t('user.enterUsername')"
                />
              </div>

              <!-- Email -->
              <div class="mb-3">
                <label class="form-label font-weight-bold">{{ $t('user.email') }}</label>
                <input
                  v-model="form.email"
                  type="email"
                  class="form-control"
                  required
                  :placeholder="$t('user.enterEmail')"
                />
              </div>

              <!-- Password -->
              <div class="mb-3">
                <label class="form-label font-weight-bold">
                  {{ $t('user.password') }} <span v-if="isEditing" class="text-muted text-sm">({{ $t('user.passwordHint') }})</span>
                </label>
                <input
                  v-model="form.password"
                  type="password"
                  class="form-control"
                  :required="!isEditing"
                  :placeholder="$t('user.enterPassword')"
                />
              </div>

              <!-- Role -->
              <div class="mb-3">
                <label class="form-label font-weight-bold">{{ $t('user.role') }}</label>
                <select v-model="form.role" class="form-select" required>
                  <option disabled value="">{{ $t('user.selectRole') }}</option>
                  <option v-for="role in roles" :key="role.id" :value="role.id">
                    {{ role.name }}
                  </option>
                </select>
              </div>

              <!-- Status Toggle (only in Edit) -->
              <div v-if="isEditing" class="form-check form-switch mt-4">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="userStatusSwitch"
                  v-model="form.is_active"
                />
                <label class="form-check-label" for="userStatusSwitch">{{ $t('user.active') }}</label>
              </div>
            </div>
            <div class="modal-footer border-0 bg-light">
              <button type="button" class="btn btn-secondary" @click="hideUserModal">
                {{ $t('common.cancel') }}
              </button>
              <button type="submit" class="btn btn-primary">
                {{ isEditing ? $t('user.updateUser') : $t('user.createUser') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api, { getUsers, createUser, patchUser, getRoles } from '../api/axios'
import * as bootstrap from 'bootstrap'
import MainPagination from '../components/MainPagination.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const users = ref([])
const roles = ref([])
const search = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const totalRecords = ref(0)
const form = ref({ username: '', email: '', password: '', role: '', is_active: true })
const isEditing = ref(false)
const errorMessage = ref('')

// Modal references
const userModal = ref(null)
let userModalInstance = null

const fetchUsers = async () => {
  try {
    const res = await getUsers({
      page: currentPage.value,
      page_size: pageSize.value,
      search: search.value
    })
    users.value = res.data.results
    totalRecords.value = res.data.count
  } catch (err) {
    console.error('Error fetching users:', err)
  }
}

const fetchRoles = async () => {
  try {
    const res = await getRoles()
    roles.value = res.data
  } catch (err) {
    console.error('Error fetching roles:', err)
  }
}

// Watch for search query
let searchTimeout = null
watch(search, () => {
  currentPage.value = 1
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    fetchUsers()
  }, 300)
})

// Watch for pagination
watch([currentPage, pageSize], () => {
  fetchUsers()
})

onMounted(() => {
  fetchUsers()
  fetchRoles()
  userModalInstance = new bootstrap.Modal(userModal.value)
})

// CRUD Handlers
const openAddModal = () => {
  isEditing.value = false
  errorMessage.value = ''
  form.value = { username: '', email: '', password: '', role: '', is_active: true }
  userModalInstance.show()
}

const openEditModal = (user) => {
  isEditing.value = true
  errorMessage.value = ''
  form.value = {
    id: user.id,
    username: user.username,
    email: user.email,
    password: '',
    role: user.role,
    is_active: user.is_active
  }
  userModalInstance.show()
}

const hideUserModal = () => userModalInstance.hide()

const saveUser = async () => {
  errorMessage.value = ''
  try {
    const data = { ...form.value }
    // If updating and password is empty, don't send it
    if (isEditing.value && !data.password) {
      delete data.password
    }

    if (isEditing.value) {
      await api.put(`/users/${data.id}/`, data)
    } else {
      await createUser(data)
    }
    
    fetchUsers()
    hideUserModal()
  } catch (err) {
    console.error(err)
    if (err.response?.data) {
      const data = err.response.data
      errorMessage.value = data.detail || 
                           data.username?.[0] || 
                           data.email?.[0] || 
                           data.password?.[0] || 
                           data.role?.[0] || 
                           'Failed to save user.'
    } else {
      errorMessage.value = 'Failed to save user.'
    }
  }
}

const toggleUserStatus = async (user) => {
  try {
    const updatedStatus = !user.is_active
    const confirmKey = updatedStatus ? t('user.confirmActivate') : t('user.confirmDeactivate')
    const confirmMsg = `${confirmKey} "${user.username}"?`
    if (!confirm(confirmMsg)) return

    await patchUser(user.id, { is_active: updatedStatus })
    fetchUsers()
  } catch (err) {
    console.error('Error toggling status:', err)
    alert(err.response?.data?.detail || 'Failed to update user status.')
  }
}
</script>

<style scoped>
.avatar-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
}
.bg-light-primary {
  background-color: var(--bs-primary-bg-subtle, #dbeafe);
  color: var(--bs-primary-text-emphasis, #084298);
}
.bg-success-subtle {
  background-color: var(--bs-success-bg-subtle, #d1fae5);
  color: var(--bs-success-text-emphasis, #0f5132);
}
.bg-danger-subtle {
  background-color: var(--bs-danger-bg-subtle, #fee2e2);
  color: var(--bs-danger-text-emphasis, #842029);
}
</style>
